import { ROUTE_TEMPLATES } from "@utils/paths";
import type { RouteDescriptor } from "intentkit";
import { generatePath, matchPath } from "react-router-dom";
import type {
  NavigationDirective,
  NavigationLocation
} from "catchment-common/interfaces/navigation";
import {
  agentRouteCatalog,
  type RouteId
} from "catchment-common/interfaces/navigation/routeCatalog";

// Bridges the route ids (shared contract) to react-router via ROUTE_TEMPLATES
// (the frontend's single home for URL shapes): matches the current URL to a
// route id (so the assistant knows where the user is) and builds a target URL
// from a navigation directive (so the assistant can route the user there).

const ROUTE_ENTRIES = Object.entries(ROUTE_TEMPLATES) as [RouteId, string][];

const definedParams = (
  params: Record<string, string | undefined>
): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) out[key] = value;
  }
  return out;
};

// The set of param names a route carries in the query string (vs the path),
// keyed by route id. Lets matchCurrentLocation read them off the URL and
// pathForDirective write them back, so the agent stays aware of e.g. the
// current Settings tab.
const QUERY_PARAMS_BY_ROUTE: Record<string, string[]> = Object.fromEntries(
  (agentRouteCatalog as readonly RouteDescriptor[]).map((r) => [
    r.url,
    (r.params ?? []).filter((p) => p.kind === "query").map((p) => p.name)
  ])
);

// Resolve the user's current location against the catalog. Returns the matched
// route id + params (path + any declared query params) when the page is a known
// route; otherwise just the path.
export const matchCurrentLocation = (pathname: string): NavigationLocation => {
  // pathname may arrive with a search string (e.g. "/settings?tab=…"); split
  // it so matchPath sees only the path and we can read declared query params.
  const [rawPath, rawSearch = ""] = pathname.split("?");
  const search = new URLSearchParams(rawSearch);
  for (const [routeId, template] of ROUTE_ENTRIES) {
    const match = matchPath({ end: true, path: template }, rawPath);
    if (match) {
      const params = definedParams(match.params);
      for (const name of QUERY_PARAMS_BY_ROUTE[routeId] ?? []) {
        const value = search.get(name);
        if (value !== null) params[name] = value;
      }
      return {
        params,
        path: pathname,
        routeId
      };
    }
  }
  return { path: pathname };
};

// Build the destination URL for a navigation directive, or undefined if the
// route id is unknown or required path params are missing. Path-kind params are
// substituted into the template; query-kind params are appended as a search
// string (URLSearchParams handles encoding, e.g. "Admins & Permissions").
export const pathForDirective = (
  directive: NavigationDirective
): string | undefined => {
  const routeId = directive.routeId as RouteId;
  const template = ROUTE_TEMPLATES[routeId];
  if (!template) return undefined;
  const queryNames = new Set(QUERY_PARAMS_BY_ROUTE[routeId] ?? []);
  const pathParams: Record<string, string> = {};
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(directive.params)) {
    if (queryNames.has(key)) {
      query.set(key, value);
    } else {
      pathParams[key] = value;
    }
  }
  try {
    const path = generatePath(template, pathParams);
    const search = query.toString();
    return search ? `${path}?${search}` : path;
  } catch {
    return undefined;
  }
};

// User-facing explainer shown in the chat after navigation. Prefers the
// route's authored `userExplainer` (set in routeCatalog.ts); falls back to a
// generic "Here's the {title} page." so the user always gets something.
//
// The catalog is declared with `as const`, which narrows each entry to its
// literal type — entries that omit `userExplainer` would not have the key
// at all in that narrowed shape. Widen to RouteDescriptor for the lookup so
// the optional field is always visible.
const ROUTES: readonly RouteDescriptor[] =
  agentRouteCatalog as readonly RouteDescriptor[];

export const explainerForDirective = (
  directive: NavigationDirective
): string => {
  const route = ROUTES.find((r) => r.url === directive.routeId);
  if (!route) return "I've taken you to the page you asked for.";
  return route.userExplainer ?? `Here's the ${route.title} page.`;
};
